import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { createImageVariantConfig } from '../../util/sdkLoader';
import { FRAME_IMAGE_FIELDS, frameImageVariantConfig } from '../../util/cardImageVariants';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// Homepage grid page size (Decision 2: newest-first, 24 per page).
export const RESULT_PAGE_SIZE = 24;

// ================ Helpers ================ //

// Buyer-actionable listings only: published, not deleted, NOT sold, and
// QA-approved (Story 7.17: metadata.qaApproved must be strictly true —
// absent = hidden, so new listings stay off the homepage until approved).
// Active + on_hold are kept (a buyer can still act on / inquire about on-hold).
// (Decision 2 — exclude sold from the default grid.)
const resultIds = data => {
  const listings = data.data || [];
  return listings
    .filter(l => {
      if (l.attributes?.deleted || l.attributes?.state !== 'published') return false;
      if (l.attributes?.publicData?.listingStatus === 'sold') return false;
      if (l.attributes?.metadata?.qaApproved !== true) return false;
      return true;
    })
    .map(l => l.id);
};

// Shared query params for the homepage listing grid. Newest-first, in-stock,
// with the same image variants + fields the ListingCard renders from.
const homeListingQueryParams = config => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return {
    // Newest-first by ACTIVITY, not creation (Story 7.9 / DECISION #317):
    // publicData.lastActivityAt (epoch seconds, tph-api-owned) refreshes on
    // first publish, successful subscription renewal, and return-from-hold, so
    // paying sellers re-surface instead of sinking by original creation date.
    // Sharetribe sort keys default to descending — UNPREFIXED
    // 'pub_lastActivityAt' is newest-first; '-pub_lastActivityAt' would be
    // oldest-first (Design System §20.1 — do NOT "fix" the direction with a
    // minus prefix; verify empirically).
    sort: 'pub_lastActivityAt',
    minStock: 1,
    stockMode: 'match-undefined',
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'deleted',
      'state',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.cardStyle',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.priceVariationsEnabled',
      'publicData.priceVariants',
      'publicData.pricingOption',
      'publicData.listingStatus',
      'publicData.holdReason',
      'publicData.birthYear',
      'publicData.color',
      'publicData.sex',
      'publicData.sire',
      'publicData.dam',
      'publicData.damsSire',
      'publicData.disciplines',
      'description',
      'metadata',
      // 🔴 Story 12.15 (AC-1) — a missing allowlist key ships the feature INERT
      // with nothing red: `readFocalPoint` treats `undefined` as centre by
      // design. Pinned in `cardFramingAllowlists.test.js`.
      'publicData.photoFocalPoints',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      ...FRAME_IMAGE_FIELDS,
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...frameImageVariantConfig(),
    'limit.images': 1,
  };
};

// ================ Thunks ================ //

const queryPayloadCreator = ({ page, config }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;
  return sdk.listings
    .query({
      ...homeListingQueryParams(config),
      page,
      perPage: RESULT_PAGE_SIZE,
    })
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      dispatch(addMarketplaceEntities(response, { listingFields }));
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error);
    });
};

// Initial / first-page fetch (also used for SSR via loadData).
export const fetchHomeListings = createAsyncThunk(
  'HomePage/fetchHomeListings',
  queryPayloadCreator
);

// Infinite-scroll append. Reuses the same payload creator; the reducer appends
// instead of replacing so accumulated listings grow as the user scrolls.
export const loadMoreHomeListings = createAsyncThunk(
  'HomePage/loadMoreHomeListings',
  queryPayloadCreator
);

// Convenience thunk: computes the next page from current state and appends it.
// Guards against over-fetching while a request is in flight or once the last
// page has been reached — keeps the page-number logic in the duck.
//
// Note: the stop condition uses totalPages (not accumulated-count vs
// totalItems). Sold listings are excluded client-side in `resultIds`, so the
// accumulated count is permanently below totalItems — comparing against it would
// never stop and would loop forever requesting empty pages (the perpetual
// spinner bug). totalPages is exact regardless of client-side filtering.
export const loadMoreNext = config => (dispatch, getState) => {
  const { viewMorePage, searchInProgress, pagination } = getState().HomePage;
  if (searchInProgress) return Promise.resolve();
  const totalPages = pagination?.totalPages;
  if (typeof totalPages === 'number' && viewMorePage >= totalPages) {
    return Promise.resolve();
  }
  return dispatch(loadMoreHomeListings({ page: viewMorePage + 1, config }));
};

// ================ Slice ================ //

const homePageSlice = createSlice({
  name: 'HomePage',
  initialState: {
    pagination: null,
    searchInProgress: false,
    searchListingsError: null,
    currentPageResultIds: [],
    // Accumulated IDs across all infinite-scroll pages. Reset on a fresh fetch.
    accumulatedResultIds: [],
    // Highest page number fetched so far; used to request the next page.
    viewMorePage: 1,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      // ── Initial fetch ────────────────────────────────────────────
      .addCase(fetchHomeListings.pending, state => {
        state.searchInProgress = true;
        state.searchListingsError = null;
        state.accumulatedResultIds = [];
        state.viewMorePage = 1;
      })
      .addCase(fetchHomeListings.fulfilled, (state, action) => {
        const ids = resultIds(action.payload.data);
        state.currentPageResultIds = ids;
        state.accumulatedResultIds = ids;
        state.pagination = action.payload.data.meta;
        state.viewMorePage = action.meta.arg.page || 1;
        state.searchInProgress = false;
      })
      .addCase(fetchHomeListings.rejected, (state, action) => {
        state.searchInProgress = false;
        state.searchListingsError = action.payload;
      })
      // ── Infinite-scroll append ───────────────────────────────────
      .addCase(loadMoreHomeListings.pending, state => {
        state.searchInProgress = true;
        state.searchListingsError = null;
      })
      .addCase(loadMoreHomeListings.fulfilled, (state, action) => {
        const newIds = resultIds(action.payload.data);
        state.currentPageResultIds = newIds;
        state.accumulatedResultIds = [...state.accumulatedResultIds, ...newIds];
        state.pagination = action.payload.data.meta;
        state.viewMorePage = state.viewMorePage + 1;
        state.searchInProgress = false;
      })
      .addCase(loadMoreHomeListings.rejected, (state, action) => {
        state.searchInProgress = false;
        state.searchListingsError = action.payload;
      });
  },
});

export default homePageSlice.reducer;

// ================ Load data (SSR-fetch first page) ================ //

export const loadData = (params, search, config) => dispatch => {
  return dispatch(fetchHomeListings({ page: 1, config }));
};
